import type { Account, Trade } from "./types";

export type TfWindow = "1Y" | "2Y" | "ALL";

export interface WindowedStats {
  return: number;          // lastEq / firstEq - 1 (0 if < 2 points)
  pnl: number;             // sum of filtered trades' pnl
  pf: number;              // grossWin / grossLoss (Infinity if no losses)
  wr: number;              // wins / total (0 if no trades)
  nTrades: number;
  equitySlice: { t: string; equity: number }[];
}

const DAY_MS = 86_400_000;

/** Cutoff timestamp (ms since epoch) for a given window, relative to now. */
export function windowCutoff(window: TfWindow, now = Date.now()): number {
  if (window === "1Y") return now - 365 * DAY_MS;
  if (window === "2Y") return now - 730 * DAY_MS;
  return 0; // ALL — epoch
}

/**
 * Slice an account's equity + trades to the chosen window and compute headline
 * stats over that slice. Divide-by-zero is guarded: pf is Infinity when there
 * are wins but no losses, NaN when there is neither.
 */
export function windowedStats(account: Account, window: TfWindow, now = Date.now()): WindowedStats {
  const cutoff = windowCutoff(window, now);

  const equitySlice = (account.equity ?? []).filter((p) => {
    const t = new Date(p.t).getTime();
    return isFinite(t) && t >= cutoff;
  });

  const trades: Trade[] = (account.trades ?? []).filter((tr) => {
    const t = new Date(tr.exit_time).getTime();
    return isFinite(t) && t >= cutoff;
  });

  const ret =
    equitySlice.length >= 2 && equitySlice[0].equity
      ? equitySlice[equitySlice.length - 1].equity / equitySlice[0].equity - 1
      : 0;

  let grossWin = 0;
  let grossLoss = 0;
  let wins = 0;
  let pnl = 0;
  for (const tr of trades) {
    const v = tr.pnl ?? 0;
    pnl += v;
    if (v > 0) {
      grossWin += v;
      wins += 1;
    } else if (v < 0) {
      grossLoss += -v;
    }
  }

  const pf = grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : NaN;
  const wr = trades.length > 0 ? wins / trades.length : 0;

  return { return: ret, pnl, pf, wr, nTrades: trades.length, equitySlice };
}

export const WINDOW_LABEL: Record<TfWindow, string> = {
  "1Y": "1 Year",
  "2Y": "2 Years",
  ALL: "All",
};
